import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faCheck, faLock, faExclamationTriangle, faCircleInfo, faUndo } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";

function CompanySection({ profile, formData, onChange, onSave, onCancel, isDirty, darkMode, t, getFieldCooldown, cooldownDays }) {
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const companyCooldown = getFieldCooldown?.("company_name");
  const addressCooldown = getFieldCooldown?.("address");

  const policyTooltip = t("To prevent fraud, certain fields (company name, address) can only be changed once every {{days}} days. Previous values are logged for security purposes.", { days: cooldownDays || 60 });

  // check if individual fields have changed from original
  const isCompanyChanged = formData.company_name !== (profile?.company_name || "");
  const isAddressChanged = formData.address !== (profile?.address || "");
  const isCityChanged = formData.city !== (profile?.city || "");

  const save = async () => {
    setSaving(true);
    setAlert(null);
    const result = await onSave();
    setSaving(false);
    
    if (result.success) {
      setAlert({ type: "success", msg: t("Changes saved") });
    } else {
      setAlert({ type: "error", msg: result.errors?.[0] || t("Failed to save") });
    }
    setTimeout(() => setAlert(null), 4000);
  };

  // reset individual field to original value
  const resetField = (field) => {
    onChange(field, profile?.[field] || "");
  };

  return (
    <div className="profile-section">
      <div className="profile-section-header">
        <div className="profile-section-icon">
          <FontAwesomeIcon icon={faBuilding} />
        </div>
        <div>
          <h2 className="profile-section-title">
            {t("Company Information")}
            <Tooltip title={policyTooltip} placement="top" arrow>
              <span className="profile-policy-icon">
                <FontAwesomeIcon icon={faCircleInfo} />
              </span>
            </Tooltip>
          </h2>
          <p className="profile-section-desc">{t("Your organization details")}</p>
        </div>
      </div>

      {alert && (
        <div className={`profile-alert ${alert.type}`}>
          <FontAwesomeIcon icon={alert.type === "success" ? faCheck : faExclamationTriangle} />
          {alert.msg}
        </div>
      )}

      <div className="profile-form-grid">
        <div className="profile-form-group full">
          <label className="profile-label">
            {t("Company Name")}
            {companyCooldown && (
              <span className="profile-cooldown-badge">
                <FontAwesomeIcon icon={faLock} />
                {companyCooldown} {t("days")}
              </span>
            )}
          </label>
          <div className="profile-input-wrap">
            <input
              type="text"
              className={`profile-input ${companyCooldown ? "readonly" : ""} ${isCompanyChanged ? "has-reset" : ""}`}
              value={formData.company_name || ""}
              onChange={(e) => onChange("company_name", e.target.value)}
              readOnly={!!companyCooldown}
            />
            {isCompanyChanged && !companyCooldown && (
              <Tooltip title={t("Reset to original")} placement="top" arrow>
                <span className="profile-input-reset" onClick={() => resetField("company_name")}>
                  <FontAwesomeIcon icon={faUndo} />
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="profile-form-group full">
          <label className="profile-label">
            {t("Address")}
            {addressCooldown && (
              <span className="profile-cooldown-badge">
                <FontAwesomeIcon icon={faLock} />
                {addressCooldown} {t("days")}
              </span>
            )}
          </label>
          <div className="profile-input-wrap">
            <input
              type="text"
              className={`profile-input ${addressCooldown ? "readonly" : ""} ${isAddressChanged ? "has-reset" : ""}`}
              value={formData.address || ""}
              onChange={(e) => onChange("address", e.target.value)}
              readOnly={!!addressCooldown}
            />
            {isAddressChanged && !addressCooldown && (
              <Tooltip title={t("Reset to original")} placement="top" arrow>
                <span className="profile-input-reset" onClick={() => resetField("address")}>
                  <FontAwesomeIcon icon={faUndo} />
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="profile-form-group">
          <label className="profile-label">{t("City")}</label>
          <div className="profile-input-wrap">
            <input
              type="text"
              className={`profile-input ${isCityChanged ? "has-reset" : ""}`}
              value={formData.city || ""}
              onChange={(e) => onChange("city", e.target.value)}
            />
            {isCityChanged && (
              <Tooltip title={t("Reset to original")} placement="top" arrow>
                <span className="profile-input-reset" onClick={() => resetField("city")}>
                  <FontAwesomeIcon icon={faUndo} />
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="profile-form-group">
          <label className="profile-label">{t("Country")}</label>
          <div className="profile-input-wrap">
            <input type="text" className="profile-input readonly" value="Canada" readOnly />
            <span className="profile-input-icon">
              <FontAwesomeIcon icon={faLock} />
            </span>
          </div>
        </div>
      </div>

      {/* Only show buttons when there are changes */}
      {isDirty && (
        <div className="profile-btn-row">
          <button className="profile-btn profile-btn-secondary" onClick={onCancel}>
            {t("Cancel")}
          </button>
          <button className="profile-btn profile-btn-primary" onClick={save} disabled={saving}>
            <FontAwesomeIcon icon={faCheck} />
            {saving ? t("Saving...") : t("Save Changes")}
          </button>
        </div>
      )}
    </div>
  );
}

export default CompanySection;
